<template>
    <modal
        size="small"
        name="partner_referral_create"
        @modalClose="modalClose"
        :title="portalContentAndDesignStore.translationSettings.portal_translation_pages_referral_create_referral"
        :cancel_btn_title="portalContentAndDesignStore.translationSettings.portal_translation_general_cancel"
    >
        <template #toggle>
            <editable-btn
                :type="portalContentAndDesignStore.getPortalLayoutButtonColorClass"
                :corners="portalContentAndDesignStore.getPortalLayoutCornersClass"
                v-model="portalContentAndDesignStore.translationSettings.portal_translation_pages_referral_create_referral"
            />
        </template>
        <template #body>
            <div class="space-y-4">
                <editable-text-field
                    text-type="span"
                    class="text-sm text-gray-500"
                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_pages_referral_create_referral_you_brought"
                />

                <alert type="error" v-if="errorMessage">{{ errorMessage }}</alert>
                <alert type="success" v-if="successMessage">{{ successMessage }}</alert>

                <div>
                    <editable-text-field
                        text-type="label"
                        for-attribute="email"
                        class="block text-gray-900 text-sm mb-1.5"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_email"
                    />
                    <text-field
                        fieldId="email"
                        @keydown.enter="createReferral"
                        autofucus="true"
                        :error="(formErrors.email ?? false)"
                        v-model="email"
                    ></text-field>
                </div>
                <div>
                    <editable-text-field
                        text-type="label"
                        for-attribute="name"
                        class="block text-gray-900 text-sm mb-1.5"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_name"
                    />
                    <text-field
                        fieldId="name"
                        @keydown.enter="createReferral"
                        :error="(formErrors.name ?? false)"
                        v-model="name"
                    ></text-field>
                </div>
                <div v-show="showMore">
                    <editable-text-field
                        text-type="label"
                        for-attribute="surname"
                        class="block text-gray-900 text-sm mb-1.5"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_last_name"
                    />
                    <text-field
                        fieldId="surname"
                        @keydown.enter="createReferral"
                        :error="(formErrors.surname ?? false)"
                        v-model="surname"
                    ></text-field>
                </div>
                <div @click.prevent="showMoreFields()" class="flex justify-end">
                    <a href="" class="flex items-center hover:underline">
                        <span class="text-xs font-medium ml-1">
                            {{
                                showMore
                                ? portalContentAndDesignStore.translationSettings.portal_translation_general_show_less_fields
                                : portalContentAndDesignStore.translationSettings.portal_translation_general_show_more_fields
                            }}
                        </span>
                    </a>
                </div>
            </div>
        </template>
        <template #footer>
            <editable-btn
                :loading="loading"
                @click="createReferral"
                type="primary"
                :disabled="loading"
                v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_create"
            />
        </template>
    </modal>
</template>

<script setup>
import {getCurrentInstance, ref} from 'vue';
import Alert from "../../../../elements/Alert";
import useFilters from "../../../../state/common/filters-and-pagination";
import {usePortalContentAndDesignStore} from "../../../../stores/partners/portalContentAndDesignStore";
import EditableBtn from "../../portal-editable-fields/EditableBtn.vue";
import EditableTextField from "../../portal-editable-fields/EditableTextField.vue";

// Define props
defineProps({
    translations: Object,
});
const {proxy} = getCurrentInstance()

const portalContentAndDesignStore = usePortalContentAndDesignStore();

// Reactive variables
const errorMessage = ref(null);
const successMessage = ref(null);
const loading = ref(false);
const formErrors = ref({});
const email = ref('');
const name = ref('');
const surname = ref('');
const showMore = ref(false);

// Create a function to handle referral creation
const createReferral = () => {
    errorMessage.value = null;
    successMessage.value = null;

    if (loading.value || !valid()) {
        return;
    }

    loading.value = true;
    axios
        .post(route('partners_int.referrals.create'), {
            email: email.value,
            name: name.value,
            surname: surname.value,
        })
        .then(response => {
            const status = parseInt(response.data.status || 0);
            if (status === 1) {
                successMessage.value = 'Referral created successfully.';

                setTimeout(() => {
                    // Emit modal close event
                    proxy.emitter.emit('modal-off', 'partner_referral_create');
                    cleanForm();
                }, 1500);

                fetchCustomersData();
            } else if (status === 0) {
                errorMessage.value = response.data?.message || 'Failed to create referral';
            }
        })
        .catch(error => {
            let errors = error.response.data.errors || false;
            errorMessage.value = error.response?.data?.message
                ? error.response.data.message
                : (Array.isArray(errors) ? Object.values(errors).flat().join(', ') : errors);
        })
        .finally(() => loading.value = false);
};

// Validation function
const valid = () => {
    let valid = true;
    formErrors.value = {};

    if (inputIsEmpty(email.value) || !/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/.test(email.value)) {
        valid = false;
        formErrors.value.email = true;
    }

    if (inputIsEmpty(name.value) || name.value.length > 100) {
        valid = false;
        formErrors.value.name = true;
    }

    return valid;
};

// Close modal
const modalClose = (name) => {
    cleanForm();
};

// Clean form function
const cleanForm = () => {
    email.value = '';
    name.value = '';
    surname.value = '';

    errorMessage.value = false;
    successMessage.value = false;
    formErrors.value = {};
    showMore.value = false;
};

// Check if input is empty
const inputIsEmpty = (value) => {
    return (value ?? '').trim().length < 1;
};

// Fetch customers data
const fetchCustomersData = () => {
    const { fetchDataBaseUrl, fetchDataFunction } = useFilters();
    fetchDataFunction.value(fetchDataBaseUrl.value);
};

// Show more fields
const showMoreFields = () => {
    showMore.value = !showMore.value;
};
</script>
